import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Tabs,
  Tab,
  Badge,
  Container,
  Zoom,
  Fade,
  Tooltip,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  OnlinePrediction as OnlineIcon,
  PowerOff as PowerOffIcon,
  CameraAlt as CameraIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getCameras, selectCameras } from "../features/cameras/cameraSlice";
import Camera from "../features/cameras/Camera";

export default function Cameras() {
  const dispatch = useDispatch();
  const { results: cameras } = useSelector(selectCameras);
  const [cameraTab, setCameraTab] = useState(0);
  const [filteredCameras, setFilteredCameras] = useState([]);
  const [stats, setStats] = useState({
    armed: 0,
    offline: 0,
    unarmed: 0,
    total: 0,
  });

  useEffect(() => {
    dispatch(getCameras());
  }, [dispatch]);

  // Helper function to check if camera is online (last reported within 1 hour)
  const isCameraOnline = useCallback((lastReportedAt) => {
    if (!lastReportedAt) return false;
    const lastReported = new Date(lastReportedAt);
    const now = new Date();
    const diffInHours = (now - lastReported) / (1000 * 60 * 60);
    return diffInHours <= 1;
  }, []);

  // Helper function to categorize camera
  const getCameraCategory = useCallback(
    (camera) => {
      const isLive = camera.live === true;
      const isOnline = isCameraOnline(camera.last_reported_at);

      if (isLive && isOnline) {
        return "armed";
      } else if (isLive && !isOnline) {
        return "offline";
      } else {
        return "unarmed";
      }
    },
    [isCameraOnline],
  );

  // Sort cameras by last_reported_at (newest first)
  const sortCamerasByLastReported = useCallback((camerasList) => {
    return [...camerasList].sort((a, b) => {
      const dateA = a.last_reported_at
        ? new Date(a.last_reported_at)
        : new Date(0);
      const dateB = b.last_reported_at
        ? new Date(b.last_reported_at)
        : new Date(0);
      return dateB - dateA;
    });
  }, []);

  // Apply camera filter based on tab
  const applyCameraFilter = useCallback(
    (tabValue) => {
      if (!cameras || cameras.length === 0) return;

      let filtered = [];

      if (tabValue === 0) {
        // Armed: live = true AND last_reported_at within 1 hour
        filtered = cameras.filter((camera) => {
          const isLive = camera.live === true;
          const isOnline = isCameraOnline(camera.last_reported_at);
          return isLive && isOnline;
        });
      } else if (tabValue === 1) {
        // Offline: live = true AND last_reported_at older than 1 hour
        filtered = cameras.filter((camera) => {
          const isLive = camera.live === true;
          const isOnline = isCameraOnline(camera.last_reported_at);
          return isLive && !isOnline;
        });
      } else if (tabValue === 2) {
        // Unarmed: live = false
        filtered = cameras.filter((camera) => {
          return camera.live === false;
        });
      }

      const sortedFiltered = sortCamerasByLastReported(filtered);
      setFilteredCameras(sortedFiltered);
    },
    [cameras, isCameraOnline, sortCamerasByLastReported],
  );

  // Update stats and filtered cameras when cameras or cameraTab changes
  useEffect(() => {
    if (cameras && cameras.length > 0) {
      let armedCount = 0;
      let offlineCount = 0;
      let unarmedCount = 0;

      cameras.forEach((camera) => {
        const category = getCameraCategory(camera);
        if (category === "armed") armedCount++;
        else if (category === "offline") offlineCount++;
        else if (category === "unarmed") unarmedCount++;
      });

      setStats({
        armed: armedCount,
        offline: offlineCount,
        unarmed: unarmedCount,
        total: cameras.length,
      });

      applyCameraFilter(cameraTab);
    }
  }, [cameras, cameraTab, applyCameraFilter, getCameraCategory]);

  const handleCameraTabChange = (event, newValue) => {
    setCameraTab(newValue);
  };

  const updateMapCenter = (lat, lng) => {
    // Handle map center update if needed
  };

  const renderCamerasContent = () => {
    return (
      <Box sx={{ mt: 0 }}>
        {/* Camera Filter Tabs */}
        <Paper
          sx={{
            borderRadius: "50px",
            overflow: "hidden",
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          }}
        >
          <Tabs
            value={cameraTab}
            onChange={handleCameraTabChange}
            variant="fullWidth"
            sx={{
              bgcolor: "#f8f9fa",
              "& .MuiTab-root": {
                py: 2,
                transition: "all 0.3s ease",
                minHeight: "50px",
                textTransform: "none",
              },
              "& .Mui-selected": {
                bgcolor: "#454545",
                color: "white !important",
                fontWeight: 700,
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            <Tab
              icon={<SecurityIcon sx={{ fontSize: "1.5rem" }} />}
              iconPosition="start"
              label={
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    ARMED{" "}
                    <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                      (Live + Online)
                    </Typography>
                    <Badge
                      badgeContent={stats.armed}
                      color="success"
                      sx={{
                        ml: 1,
                        "& .MuiBadge-badge": {
                          fontSize: "0.7rem",
                          height: "20px",
                          minWidth: "20px",
                          borderRadius: "10px",
                          position: "relative",
                          transform: "none",
                          bgcolor: "#2e7d32",
                        },
                      }}
                    />
                  </Typography>
                </Box>
              }
              sx={{
                color: cameraTab === 0 ? "white" : "#2e7d32",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                "&.Mui-selected": {
                  color: "white !important",
                },
              }}
            />
            <Tab
              icon={<OnlineIcon sx={{ fontSize: "1.5rem" }} />}
              iconPosition="start"
              label={
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    OFFLINE{" "}
                    <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                      (No report `&gt;` 1 hour)
                    </Typography>
                    <Badge
                      badgeContent={stats.offline}
                      color="warning"
                      sx={{
                        ml: 1,
                        "& .MuiBadge-badge": {
                          fontSize: "0.7rem",
                          height: "20px",
                          minWidth: "20px",
                          borderRadius: "10px",
                          position: "relative",
                          transform: "none",
                          bgcolor: "#ed6c02",
                        },
                      }}
                    />
                  </Typography>
                </Box>
              }
              sx={{
                color: cameraTab === 1 ? "white" : "#ed6c02",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                "&.Mui-selected": {
                  color: "white !important",
                },
              }}
            />
            <Tab
              icon={<PowerOffIcon sx={{ fontSize: "1.5rem" }} />}
              iconPosition="start"
              label={
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    UNARMED{" "}
                    <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                      (Live OFF)
                    </Typography>
                    <Badge
                      badgeContent={stats.unarmed}
                      color="error"
                      sx={{
                        ml: 1,
                        "& .MuiBadge-badge": {
                          fontSize: "0.7rem",
                          height: "20px",
                          minWidth: "20px",
                          borderRadius: "10px",
                          position: "relative",
                          transform: "none",
                          bgcolor: "#d32f2f",
                        },
                      }}
                    />
                  </Typography>
                </Box>
              }
              sx={{
                color: cameraTab === 2 ? "white" : "#d32f2f",
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                "&.Mui-selected": {
                  color: "white !important",
                },
              }}
            />
          </Tabs>
        </Paper>

        {/* Camera Grid */}
        <Grid container spacing={2} sx={{ mt: 1, justifyContent: "center" }}>
          {filteredCameras.length === 0 ? (
            <Grid item xs={12}>
              <Fade in={true}>
                <Paper
                  sx={{
                    p: 4,
                    textAlign: "center",
                    bgcolor: "#f8f9fa",
                    borderRadius: "20px",
                  }}
                >
                  <Typography variant="h6" sx={{ color: "#666", mb: 1 }}>
                    {cameraTab === 0
                      ? "No armed cameras available"
                      : cameraTab === 1
                        ? "No offline cameras available"
                        : "No unarmed cameras available"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#999" }}>
                    {cameraTab === 0
                      ? "All cameras are either offline or unarmed"
                      : cameraTab === 1
                        ? "All live cameras are reporting within the last hour"
                        : "No cameras are currently disarmed"}
                  </Typography>
                </Paper>
              </Fade>
            </Grid>
          ) : (
            <>
              {filteredCameras.map((camera, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  xl={2.4}
                  key={camera.id || index}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Zoom
                    in={true}
                    style={{ transitionDelay: `${index * 50}ms` }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      <Camera
                        content={camera}
                        updateMapCenter={updateMapCenter}
                      />
                    </Box>
                  </Zoom>
                </Grid>
              ))}
            </>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Paper
        sx={{
          background:
            "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
          padding: "5px 20px",
          color: "#fff",
          mb: 2,
          borderRadius: "15px",
          boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <CameraIcon sx={{ fontSize: 30 }} />
            <Typography variant="h5" fontWeight={600}>
              Cameras
            </Typography>
            <Badge
              badgeContent={stats.total}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  bgcolor: "#fff",
                  color: "#454545",
                  fontWeight: "bold",
                },
              }}
            />
          </Box>
          <Tooltip title="Refresh">
            <Box
              onClick={() => dispatch(getCameras())}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.2)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <RefreshIcon />
              <Typography variant="body2">Refresh</Typography>
            </Box>
          </Tooltip>
        </Box>
      </Paper>

      {/* Camera Content */}
      <Container maxWidth="xl" sx={{ p: 0 }}>
        {renderCamerasContent()}
      </Container>
    </Box>
  );
}
